import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import GTM from "../../GTM";
import useOutsideClick from "../../hooks/useOutsideClick";

function ModifyViewButton({ table, attribute2, setIsResetButtonEnabled, setEnableSaveButton }) {
    const [isDropdownVisible, setDropdownVisibility] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const gtm = new GTM(null);

    const dropdownRef = useRef(null);
    const toggleRef = useRef(null);

    const toggleDropdown = () => {
        setDropdownVisibility(!isDropdownVisible);
        gtm.Event(
            "custom_event",
            "button",
            "click",
            "modify view",
            "report detail",
            attribute2,
            "field_interaction",
            ""
        );
    };

    const isClickedOutside = useOutsideClick(dropdownRef, toggleRef);

    useEffect(() => {
        if (!isClickedOutside) return;

        setDropdownVisibility(false);
        setSearchQuery("");
        setFocusedIndex(0);
    }, [isClickedOutside])

    useEffect(() => {
        if (isDropdownVisible) {
            const firstFocusableElement =
                document.querySelector(".dropdown-search");
            if (firstFocusableElement) {
                firstFocusableElement.focus();
            }
        }
    }, [isDropdownVisible]);

    const filteredColumns = table
        .getAllLeafColumns()
        .filter((column) =>
            column.columnDef.header
                .toLowerCase()
                .includes(searchQuery.toLowerCase())
        );

    const clearSearch = () => {
        setSearchQuery("");
    };

    const handleKeyDown = (e) => {
        if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();

        if (isDropdownVisible) {
            switch (e.key) {
                case "ArrowDown":
                    setFocusedIndex((prev) =>
                        Math.min(filteredColumns.length, prev + 1)
                    );
                    break;
                case "ArrowUp":
                    setFocusedIndex((prev) => Math.max(0, prev - 1));
                    break;
                case "Enter":
                case " ":
                    if (focusedIndex === 0) {
                        table.getToggleAllColumnsVisibilityHandler()();
                    } else if (focusedIndex > 0) {
                        const column = filteredColumns[focusedIndex - 1];
                        if (column?.getCanHide()) {
                            column.getToggleVisibilityHandler()();
                        }
                    }
                    break;
                case "Escape":
                    setDropdownVisibility(false);
                    break;
                default:
                    break;
            }
        } else if (
            e.key === "ArrowDown" ||
            e.key === "Enter" ||
            e.key === " "
        ) {
            setDropdownVisibility(true);
            setFocusedIndex(0);
        }
    };

    useEffect(() => {
        if (isDropdownVisible) {
            const focusableItems =
                document.querySelectorAll(".dropdown-search");
            if (focusableItems[focusedIndex]) {
                focusableItems[focusedIndex].focus();
            }
        }
    }, [focusedIndex, isDropdownVisible]);

    const trackGTMEvent = ({ label, attribute4 }) => {
        gtm.Event(
            "custom_event",
            "dropdown",
            "click",
            label,
            "report detail",
            attribute2,
            "filter_report_columns",
            attribute4,
        );
    };

    return (
        <div
            className="col-lg-2 col-sm-2 col-2"
            style={{ width: "fit-content" }}
        >
            <div className="dropdown">
                <button
                    className="modify-view"
                    onClick={toggleDropdown}
                    onKeyDown={handleKeyDown}
                    aria-expanded={isDropdownVisible}
                    aria-controls="multi-select-dropdown"
                    aria-label="Columns"
                    tabIndex={0}
                    ref={toggleRef}
                >
                    <span id="modify-columns" className="sr-only">
                        Use the arrow keys to navigate options, Enter to select,
                        and Escape to close.
                    </span>
                    {" "}
                    Columns <i className="fa-solid fa-plus-minus"></i>
                </button>
                {isDropdownVisible && (
                    <div
                        id="myDropdown"
                        className={`dropdown-content show`}
                        onClick={(e) => e.stopPropagation()}
                        tabIndex={0}
                        onKeyDown={handleKeyDown}
                        role="listbox"
                        aria-label="Use the arrow keys to navigate options, Enter to select, and Escape to close."
                        ref={dropdownRef}
                    >
                        <div className="dropdown-header">Columns displayed</div>
                        <div className="search-container">
                            <i className="fa-solid fa-magnifying-glass icon mx-4p"></i>
                            <input
                                className="dropdown-search"
                                type="text"
                                placeholder="Search"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                tabIndex={0}
                                aria-label="Search columns"
                            />
                            {searchQuery && (
                                <div
                                    className="icon cursor-pointer"
                                    onClick={clearSearch}
                                    tabIndex={0}
                                    role="button"
                                    aria-label="Clear search"
                                    onKeyDown={(e) => {
                                        if (
                                            e.key === "Enter" ||
                                            e.key === " "
                                        ) {
                                            e.preventDefault();
                                            clearSearch();
                                        }
                                    }}
                                >
                                    <i className="fa-solid fa-xmark"></i>
                                </div>
                            )}
                        </div>

                        <div
                            className="custom-scrollbar"
                            style={{
                                maxHeight: 200,
                            }}
                        >
                            {filteredColumns.length === 0 && (
                                <div className="d-flex justify-content-center my-8p text-center">
                                    No options
                                </div>
                            )}

                            <div>
                                {filteredColumns.length > 0 && (
                                    <div>
                                        <label className="dropdown-list">
                                            <input
                                                type="checkbox"
                                                checked={table.getIsAllColumnsVisible()}
                                                onChange={(e) => {
                                                    table.getToggleAllColumnsVisibilityHandler()(
                                                        e
                                                    );
                                                    trackGTMEvent({
                                                        label: e.target.checked
                                                            ? "check"
                                                            : "uncheck",
                                                        attribute4: e.target
                                                            .checked
                                                            ? "select all"
                                                            : "deselect all",
                                                    });
                                                    setIsResetButtonEnabled(true)
                                                    setEnableSaveButton(true);
                                                }}
                                                tabIndex={0}
                                                aria-label="Select all columns"
                                            />{" "}
                                            Select All
                                        </label>
                                    </div>
                                )}
                                {filteredColumns.map((column, index) => (
                                    <div
                                        key={column.id}
                                        role="option"
                                        aria-selected={
                                            focusedIndex === index + 1
                                        }
                                        style={{
                                            ...(column.getCanHide()
                                                ? {}
                                                : { pointerEvents: "none" }),
                                            ...(column.getIsVisible()
                                                ? { cursor: "pointer" }
                                                : {}),
                                        }}
                                    >
                                        <label className="dropdown-list">
                                            <input
                                                type="checkbox"
                                                checked={column.getIsVisible()}
                                                onChange={(e) => {
                                                    column.getToggleVisibilityHandler()(
                                                        e
                                                    );
                                                    trackGTMEvent({
                                                        label: e.target.checked
                                                            ? "check"
                                                            : "uncheck",
                                                        attribute4:
                                                            column?.columnDef
                                                                ?.header,
                                                    });
                                                    setIsResetButtonEnabled(true)
                                                    setEnableSaveButton(true);
                                                }}
                                                style={
                                                    column.getCanHide()
                                                        ? {}
                                                        : {
                                                            backgroundColor:
                                                                "#AAAAAA",
                                                        }
                                                }
                                                tabIndex={
                                                    column.getCanHide() ? 0 : -1
                                                }
                                                aria-label={`Toggle visibility for ${column.id}`}
                                            />{" "}
                                            {column.columnDef?.header}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

ModifyViewButton.propTypes = {
    // Existing props ...
    table: PropTypes.shape({
        getState: PropTypes.func,
        setColumnOrder: PropTypes.func,
    }).isRequired,
    attribute2: PropTypes.any,
    setIsResetButtonEnabled: PropTypes.func.isRequired,
    setEnableSaveButton: PropTypes.func.isRequired,
};

export default ModifyViewButton;
